import React, { useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Download, Loader2, Pencil } from 'lucide-react';
import { toast } from 'sonner';
import { tesisTuruList, tesisTuruLabel } from '../utils/tesisTuru';
import { exportEmlakDetayPDF } from '../utils/emlakDetayPdf';
import { useEmlakKonum } from './modules/emlak/useEmlakKonum';
import { turInfo, emlakBaslik, hasDepremYangin } from './modules/emlak/emlakOrtak';
import { TemelBilgiler, KonumKadastro, MulkiyetTahsis, DepremYangin, AciklamaKayit } from './modules/emlak/EmlakDetailSections';

// Emlak sihirbazının adımlarıyla aynı numaralı bölümler (İhaleler detay kartı deseni)
export default function EmlakDetailDialog({ emlak, open, onClose, onEdit, canEdit = false }) {
  const geo = useEmlakKonum(emlak, open);
  const [pdfBusy, setPdfBusy] = useState(false);
  if (!emlak) return null;

  const tur = turInfo(emlak.tasinmaz_turu);
  const title = emlakBaslik(emlak);
  const konum = [emlak.il || 'İstanbul', emlak.ilce, emlak.mahalle].filter(Boolean).join(' / ');
  const sections = [
    ['temel', TemelBilgiler], ['konum', KonumKadastro], ['mulkiyet', MulkiyetTahsis],
    hasDepremYangin(emlak) && ['deprem', DepremYangin], ['aciklama', AciklamaKayit],
  ].filter(Boolean);

  const handlePdf = async () => {
    setPdfBusy(true);
    try {
      await exportEmlakDetayPDF(emlak, { geo });
    } catch (err) {
      if (!/İzleyici/i.test(err?.message || '')) toast.error('PDF oluşturulamadı: ' + (err?.message || 'bilinmeyen hata'));
    } finally {
      setPdfBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="bg-gray-900 border-gray-800 text-white max-w-5xl max-h-[88vh] overflow-y-auto" data-testid="emlak-detail-dialog">
        <DialogHeader>
          <DialogTitle className="text-blue-400" data-testid="emlak-detail-title">Taşınmaz Detayları: {title}</DialogTitle>
          <DialogDescription className="text-gray-400" data-testid="emlak-detail-konum-ozet">{konum} — {tur.label} Bilgi Notu</DialogDescription>
          <div className="flex flex-wrap items-center gap-1.5 pt-1">
            <Badge className={`border ${tur.tone}`} data-testid="emlak-detail-tur-badge">{tur.label}</Badge>
            {tesisTuruList(emlak.tesis_turu).map((code) => (
              <Badge key={code} className="bg-indigo-900/50 text-indigo-300 border border-indigo-700" data-testid={`emlak-detail-tesis-turu-badge-${code}`}>{tesisTuruLabel(code)}</Badge>
            ))}
            {emlak.mulkiyet === 'il_mudurlugu_kamulastirma' && <Badge className="bg-amber-900/50 text-amber-300 border border-amber-700">Kamulaştırma</Badge>}
            {emlak.tahsis_verildi_mi === 'kiralandi' && <Badge className="bg-fuchsia-900/50 text-fuchsia-300 border border-fuchsia-700">Kiralık Taşınmaz</Badge>}
          </div>
        </DialogHeader>
        <div className="space-y-5 py-2">
          {sections.map(([key, Cmp], i) => <Cmp key={key} emlak={emlak} geo={geo} no={String(i + 1)} />)}
        </div>
        <DialogFooter>
          {canEdit && onEdit && (
            <Button onClick={() => { onClose(); onEdit(emlak); }} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="emlak-detail-edit-btn">
              <Pencil className="w-4 h-4 mr-2" /> Düzenle
            </Button>
          )}
          <Button onClick={handlePdf} disabled={pdfBusy} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="emlak-detail-pdf-btn">
            {pdfBusy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
            {pdfBusy ? 'PDF hazırlanıyor…' : 'PDF İndir'}
          </Button>
          <Button onClick={onClose} className="bg-gray-800 text-white hover:bg-gray-700" data-testid="emlak-detail-close-btn">Kapat</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
